import { Button, ConfirmDialog } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useDeleteDevice,
  useDeviceBasePrompt,
  useDeviceBases,
  useDeviceCallsPrompt,
  useDeviceCredentialsPrompt,
  useRingDevice,
  useUpdateDevice,
} from "@/features/devices/hooks/use-devices";
import type { Device } from "@/features/devices/types";
import { useClientId } from "@/features/utils/api-scope";
import { useTranslation } from "@/features/utils/i18n";
import {
  Bell,
  ClockCounterClockwise,
  DeviceMobile,
  DotsThree,
  Key,
  Pause,
  PencilSimple,
  Play,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import {
  DeviceForm,
  emptyDeviceForm,
  toDeviceInput,
  type DeviceFormData,
} from "./device-form";

export const DeviceAction = ({ device }: { device: Device }) => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();

  // Page-level, like the credentials dialog: the list refetches on an interval
  // and a dialog owned by a table cell does not reliably survive that.
  const { show: showCalls } = useDeviceCallsPrompt();
  const { show: showBase } = useDeviceBasePrompt();
  const { byDevice: baseOf } = useDeviceBases(clientId || "");

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [form, setForm] = useState<DeviceFormData>(emptyDeviceForm(device));

  // Handed to the page, which owns the dialog. This component is a table cell
  // and may be unmounted by any refetch — it is the wrong place to hold the
  // only copy of a password.
  const { showConnection } = useDeviceCredentialsPrompt();

  const ringDevice = useRingDevice();
  const updateDevice = useUpdateDevice();
  const deleteDevice = useDeleteDevice();

  const handleRing = async () => {
    if (!clientId) return;
    try {
      await ringDevice.mutateAsync({ clientId, deviceId: device.id! });
      toast.success(t("devices.messages.ringing"));
    } catch (error) {
      // The server answers with a reason code rather than prose, because the
      // reason is the whole value of this button: "not_registered" tells the
      // client to go look at the phone, "asterisk_unavailable" tells them to
      // call us.
      const reason = error instanceof Error ? error.message : "";
      toast.error(
        t([`devices.ringErrors.${reason}`, "devices.messages.ringFailed"]),
      );
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) return;
    try {
      await updateDevice.mutateAsync({
        clientId,
        deviceId: device.id!,
        data: toDeviceInput(form),
      });
      setEditOpen(false);
      toast.success(t("devices.messages.updated"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("devices.messages.updateFailed"),
      );
    }
  };

  const handleToggleSuspend = async () => {
    if (!clientId) return;
    const status = device.status === "suspended" ? "active" : "suspended";
    try {
      await updateDevice.mutateAsync({
        clientId,
        deviceId: device.id!,
        data: { status },
      });
      toast.success(
        status === "suspended"
          ? t("devices.messages.suspended")
          : t("devices.messages.resumed"),
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("devices.messages.updateFailed"),
      );
    }
  };


  const handleDelete = async () => {
    if (!clientId) return;
    try {
      await deleteDevice.mutateAsync({ clientId, deviceId: device.id! });
      toast.success(t("devices.messages.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("devices.messages.deleteFailed"),
      );
    }
  };

  return (
    <div className="flex items-center justify-end gap-2">
      {/*
        The history is what gets opened day to day; ringing is the acceptance
        test of the provisioning chain, used once per install, so it lives in
        the menu.
      */}
      <Button variant="outline" size="sm" onClick={() => showCalls(device)}>
        <ClockCounterClockwise className="mr-2 h-4 w-4" />
        {t("devices.actions.calls")}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <DotsThree className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/*
            Disabled while offline: Asterisk has nowhere to send the INVITE, and
            an error toast is a worse answer than a control that visibly cannot
            be used.
          */}
          <DropdownMenuItem
            onClick={handleRing}
            disabled={!device.registered || ringDevice.isPending}
          >
            <Bell className="mr-2 h-4 w-4" />
            {t("devices.actions.ring")}
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => {
              setForm(emptyDeviceForm(device));
              setEditOpen(true);
            }}
          >
            <PencilSimple className="mr-2 h-4 w-4" />
            {t("devices.actions.edit")}
          </DropdownMenuItem>

          {/*
            The credentials of the phone itself — the web interface of its base,
            the PIN the base asks for — as opposed to the SIP account below,
            which is ours. Disabled when the handset is on no base: the entry
            would open a dialog about nothing, and the way to fix that is the
            base field in "Modifier", one line up.
          */}
          <DropdownMenuItem
            disabled={!baseOf.get(device.id!)}
            onClick={() => {
              const base = baseOf.get(device.id!);
              if (base) showBase(base.id!);
            }}
          >
            <DeviceMobile className="mr-2 h-4 w-4" />
            {t("devices.actions.base")}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleToggleSuspend}>
            {device.status === "suspended" ? (
              <Play className="mr-2 h-4 w-4" />
            ) : (
              <Pause className="mr-2 h-4 w-4" />
            )}
            {device.status === "suspended"
              ? t("devices.actions.resume")
              : t("devices.actions.suspend")}
          </DropdownMenuItem>

          {/*
            Reading, not re-issuing. The settings a phone needs are the common
            question and were only ever visible in the seconds after creation;
            issuing new credentials — the destructive answer to "I lost them" —
            now lives inside that dialog, behind its own confirmation.
          */}
          <DropdownMenuItem
            onClick={() =>
              showConnection(
                device.id!,
                device.connection,
                device.sip_password_hint,
              )
            }
          >
            <Key className="mr-2 h-4 w-4" />
            {t("devices.actions.credentials")}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash className="mr-2 h-4 w-4" />
            {t("devices.actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <form onSubmit={handleSave}>
            <DialogHeader>
              <DialogTitle>{t("devices.edit.title")}</DialogTitle>
              <DialogDescription>{t("devices.edit.description")}</DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <DeviceForm value={form} onChange={setForm} />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
              >
                {t("devices.actions.cancel")}
              </Button>
              <Button type="submit" disabled={updateDevice.isPending}>
                {t("devices.actions.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/*
        Deleting takes the phone off the line: its SIP account goes with it and
        the handset stops registering — unlike a base, nothing to retype.
      */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t("devices.delete.title")}
        description={t("devices.delete.description", { name: device.label })}
        confirmLabel={t("devices.actions.delete")}
        cancelLabel={t("devices.actions.cancel")}
        confirmDisabled={deleteDevice.isPending}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
};
